import { generateDownloadUrl } from "./generateDownloadUrl";

export interface DownloadOutputOptions {
  /** Filename to request from the backend (overrides the server-derived name). */
  requestedFilename?: string;
}

/**
 * Resolve a download target to a FRESH presigned URL and trigger the browser download.
 *
 * `target` may be either a bare S3 object key OR a (possibly-expired) full S3 presigned
 * URL — older job records and realtime completion pushes baked a 1-hour URL into the
 * job's `outputLocation`. In BOTH cases we extract the S3 key and re-sign on-demand via
 * `/utility/download`, so the link is always valid at click time rather than reusing a
 * stale URL that S3 rejects with `AccessDenied: Request has expired` (Trello #404).
 *
 * This is the single download primitive for every result surface (transform, Q&A,
 * upload-completion modal, recent list, generic list) — do not reintroduce a per-component
 * "if it starts with http, use it directly" branch.
 */
export async function downloadOutput(
  target: string,
  options: DownloadOutputOptions = {},
): Promise<void> {
  const trimmed = target.trim();
  if (!trimmed) return;

  // A baked presigned URL looks like `https://<bucket>.s3.<region>.amazonaws.com/<key>?X-Amz-...`.
  // Pull the bare key out of the path so we re-sign instead of reusing the stale URL.
  let fileKey = trimmed;
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      fileKey = decodeURIComponent(new URL(trimmed).pathname.replace(/^\/+/, ""));
    } catch {
      fileKey = "";
    }
  }

  // If we couldn't resolve a key, bail — never navigate to a raw, unverified URL.
  if (!fileKey) return;

  const { downloadUrl, filename } = await generateDownloadUrl(fileKey, options.requestedFilename);

  const link = document.createElement("a");
  link.href = downloadUrl;
  if (filename) link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
